/**
 * RFC 1881 Zori Editing — ClinicalImpression extension URLs and Langfuse score
 * names aligned with agents `fountain_agents.lib.constants` / `lib.feedback`.
 *
 * RFC 1885 (Zori KB) stresses observability for Zori surfaces; recording hunk
 * decisions as Langfuse scores on the **suggestion** trace supports retrieval
 * quality and product analytics.
 */
/** Root Langfuse trace id for the suggest-agent run that produced the draft diff */
export const FOUNTAIN_SUGGESTION_LANGFUSE_TRACE_ID_EXTENSION =
  'https://lifeomic.com/fhir/zori-suggestion-langfuse-trace-id';

export const LANGFUSE_SCORE_SUGGESTION_HUNK_ACCEPTED =
  'suggestion-hunk-accepted';

export const LANGFUSE_SCORE_SUGGESTION_HUNK_REJECTED =
  'suggestion-hunk-rejected';

export type SuggestionHunkDecision = 'accepted' | 'rejected';

/** `agent_name` for `POST /v1/agents-v2/feedback` when scoring suggest-agent runs. */
export const SUGGEST_AGENT_NAME = 'suggest-agent';

export type SuggestionScoreFeedbackInput = {
  agentName: string;
  traceId: string;
  feedback: string;
  score: number;
  scoreName: string;
};

/**
 * Payload fields for `POST /v1/agents-v2/feedback` when logging per-hunk review
 * on the suggest-agent Langfuse trace (see impression extension
 * {@link FOUNTAIN_SUGGESTION_LANGFUSE_TRACE_ID_EXTENSION}).
 */
export function buildSuggestionHunkFeedbackPayload(
  decision: SuggestionHunkDecision,
  detail?: string,
): { score: number; score_name: string; feedback: string } {
  const score = decision === 'accepted' ? 5 : 1;
  const score_name =
    decision === 'accepted'
      ? LANGFUSE_SCORE_SUGGESTION_HUNK_ACCEPTED
      : LANGFUSE_SCORE_SUGGESTION_HUNK_REJECTED;
  const suffix = detail?.trim() ? `: ${detail.trim()}` : '';
  const feedback =
    decision === 'accepted'
      ? `Zori suggestion hunk accepted${suffix}`
      : `Zori suggestion hunk rejected${suffix}`;
  return { score, score_name, feedback };
}

/**
 * Fire-and-forget Langfuse score on the suggestion trace (no-op without trace id).
 * Swallows errors so diff UX is never blocked by telemetry.
 */
export function fireSuggestionTraceScore(
  send: (input: SuggestionScoreFeedbackInput) => Promise<unknown>,
  suggestionTraceId: string | undefined,
  decision: SuggestionHunkDecision,
  detail?: string,
): void {
  if (!suggestionTraceId?.trim()) {
    return;
  }
  const { score, score_name, feedback } = buildSuggestionHunkFeedbackPayload(
    decision,
    detail,
  );
  void send({
    agentName: SUGGEST_AGENT_NAME,
    traceId: suggestionTraceId.trim(),
    feedback,
    score,
    scoreName: score_name,
  }).catch(() => {
    /* telemetry only */
  });
}

/** One side (accepted | rejected) of a batched review session. */
export type SuggestionFeedbackBucket = {
  /** Total hunks resolved this way (individual clicks + bulk actions). */
  count: number;
  /** Per-hunk previews; bulk actions contribute to `count` without a preview. */
  details: string[];
};

export type SuggestionFeedbackBatch = {
  accepted: SuggestionFeedbackBucket;
  rejected: SuggestionFeedbackBucket;
};

/** Cap the bullets embedded in the Slack/Langfuse comment so it stays readable. */
const MAX_FEEDBACK_DETAILS = 10;

function summarizeBucket(
  decision: SuggestionHunkDecision,
  bucket: SuggestionFeedbackBucket,
): { score: number; score_name: string; feedback: string } {
  const score = decision === 'accepted' ? 5 : 1;
  const score_name =
    decision === 'accepted'
      ? LANGFUSE_SCORE_SUGGESTION_HUNK_ACCEPTED
      : LANGFUSE_SCORE_SUGGESTION_HUNK_REJECTED;
  const noun = bucket.count === 1 ? 'hunk' : 'hunks';
  const details = bucket.details.map(d => d.trim()).filter(Boolean);
  const shown = details.slice(0, MAX_FEEDBACK_DETAILS);
  const bullets = shown.map(d => `- ${d}`).join('\n');
  const remaining = details.length - shown.length;
  const more = remaining > 0 ? `\n- …and ${remaining} more` : '';
  const detailBlock = bullets ? `\n${bullets}${more}` : '';
  const feedback = `Zori suggestion ${decision} ${bucket.count} ${noun}${detailBlock}`;
  return { score, score_name, feedback };
}

/**
 * Consolidate a whole review session into at most two feedback payloads — one
 * for accepted hunks, one for rejected — instead of one per hunk click. This is
 * the batching counterpart to {@link fireSuggestionTraceScore}: it preserves the
 * distinct accepted/rejected Langfuse score names (so analytics are unchanged)
 * while collapsing N per-hunk `POST /v1/agents-v2/feedback` calls (and their
 * Slack notifications) down to ≤2 per suggestion trace.
 *
 * Returns `[]` for a blank trace id or an empty batch.
 */
export function buildSuggestionFeedbackBatch(
  suggestionTraceId: string,
  batch: SuggestionFeedbackBatch,
): SuggestionScoreFeedbackInput[] {
  const traceId = suggestionTraceId.trim();
  if (!traceId) {
    return [];
  }
  const payloads: SuggestionScoreFeedbackInput[] = [];
  const directions: SuggestionHunkDecision[] = ['accepted', 'rejected'];
  for (const decision of directions) {
    const bucket = decision === 'accepted' ? batch.accepted : batch.rejected;
    if (bucket.count <= 0) {
      continue;
    }
    const { score, score_name, feedback } = summarizeBucket(decision, bucket);
    payloads.push({
      agentName: SUGGEST_AGENT_NAME,
      traceId,
      feedback,
      score,
      scoreName: score_name,
    });
  }
  return payloads;
}
